<template>
  <div
    :class="[
      'card w-100 shadow-sm',
      {'selected-card': isSelected},
      {'border border-primary-subtle border-2': newsItem.unread || false},
    ]"
    @click="$emit('update', newsItem)"
  >
    <div class="card-body">
      <div
        v-if="editable"
        class="edit-button d-flex position-absolute top-0 end-0 m-2 border rounded"
        style="background-color: var(--bs-body-bg)"
      >
        <a :href="getEditLink(newsItem)" target="_blank" @click.stop>
          <IconButton
            icon-name="material-symbols:edit-square"
            variant="ghost"
            class="news-icon-button"
          />
        </a>
      </div>
      <p>
        <span class="text-primary">
          <Icon
            :icon="
              Array.isArray(newsItem.tags) && newsItem.tags.includes('RSS')
                ? 'material-symbols:rss-feed'
                : 'material-symbols:info-outline'
            "
            class="mb-1 flex-shrink-0"
          />
          {{ newsItem.meta.title }}
        </span>
        <span v-if="newsItem.introduction"> - {{ newsItem.introduction }}</span>
      </p>
      <footer>
        <sup class="fst-italic">
          <span v-if="areDatesDifferent(newsItem)">
            {{ translate('Last updated') }}: {{ getPublicationDate(newsItem) }}<br />
          </span>
          {{ translate('Created') }}: {{ getCreationDate(newsItem) }}
        </sup>
      </footer>
    </div>
  </div>
</template>

<script setup lang="ts">
import config from '@/config';
import {NewsItem} from '@/stores/apolloPlatform/news/news';

interface Props {
  newsItem: NewsItem;
  isSelected: boolean;
  editable?: boolean;
}

withDefaults(defineProps<Props>(), {
  editable: false,
});

defineEmits<{
  update: [newsItem: NewsItem];
}>();

const {displayTimeStamp, translate} = useLocalisation();

const getPublicationDate = (newsItem: NewsItem) => {
  const date = new Date(Date.parse(newsItem.meta.last_published_at || ''));
  return displayTimeStamp(date);
};

const getCreationDate = (newsItem: NewsItem) => {
  const date = new Date(Date.parse(newsItem.meta.first_published_at || ''));
  return displayTimeStamp(date);
};

const areDatesDifferent = (newsItem: NewsItem) => {
  return getPublicationDate(newsItem) !== getCreationDate(newsItem);
};

const getEditLink = (item: NewsItem) => {
  if (!item.edit_url) return '#';
  return new URL(item.edit_url, config.CMS_ADMIN_URL);
};
</script>

<style scoped>
.card {
  transition: background-color 0.3s;
}

.card:hover {
  background-color: var(--bs-tertiary-bg);
  cursor: pointer;
}

.selected-card {
  background-color: var(--bs-tertiary-bg);
}

.edit-button {
  visibility: hidden;
  opacity: 0;
  transition:
    opacity 0.3s,
    visibility 0.3s;
}

.card:hover .edit-button {
  visibility: visible;
  opacity: 1;
}
</style>
